// traceActions - one record per thing the agent did, for the Lanes view.
//
// Lanes reads a trace through several doors: the committed extractors, the
// code-mode unwrap's copies of a cell's inner calls, the shell grammar, a
// model's verified readings. Each door's packets are ops on a lane at a turn.
// An action is the call behind them: the tool_use the agent issued, the calls
// it made (a code cell's inner calls, or the call itself), and what each call
// returned, paired once. A door that reads actions sees each call exactly
// once, and every packet can name the action it came from, so a lane cell can
// say what else the same call touched.
//
// Built over the unpacked turns, where a cell's inner calls are the copies the
// unwrap spliced in after it. A cell the unwrap did not expand (its grammar is
// not loaded, or the trace is past the unwrap's cell cap) still yields the one
// shell call the Codex exec wrapper states, read without the grammar and with
// the host's envelope stripped from its result.

import { codexWrappedArgs, shellToolCommand } from "./shellToolCall.js";
import { toolResultText } from "./parseApiRequest.js";
import { MAX_SHELL_CALLS, detectShellLanguage } from "./traceCapability.js";
import {
  cellOutcome,
  isCodeModeCell,
  normalizeShellCommand,
  waitEnvelopesByCell,
} from "./traceCodeModeCells.js";

const UNPAIRED = Object.freeze({ text: null, ok: true, paired: false });

// What a call returned. Failure is carried apart from the text: a failed read
// attaches no content - what came back is an error message, not the file - but
// a failed build is what a build lane exists to show, so the outcome survives
// when the output does not.
function pairedOf(block) {
  if (!block) return UNPAIRED;
  return { text: block.isError ? null : toolResultText(block.content), ok: !block.isError, paired: true };
}

function callOf(block, turnIndex, blockIndex, result) {
  const name = String(block.name || "tool");
  const command = shellToolCommand(name, block.input);
  return {
    key: block.id,
    turnIndex,
    blockIndex,
    tool: block.codeMode?.tool || name,
    name,
    input: block.input,
    command,
    language: command ? detectShellLanguage(name, command) : null,
    result,
  };
}

/**
 * Every action in a trace.
 *
 * @param turns  the unpacked turns the Lanes view reads
 * @returns {{ actions: Array, byId: Map }} actions in block order. An action:
 *   { id, turnIndex, blockIndex, tool, form: "cell" | "shell" | "tool", calls }
 *   where each call is { key, turnIndex, blockIndex, tool, name, input,
 *   command, language, result: { text, ok, paired } } and `key` is the block
 *   carrying the call: a copy for a cell's inner call, else the action itself.
 */
export function buildTraceActions(turns) {
  const list = Array.isArray(turns) ? turns : [];
  // Results by the call they answer, in trace order: a call's result is the
  // first one at or after the call's own turn, so a repeated positional id
  // never pairs with a result that came before it.
  const results = new Map();
  const firstResult = new Map();
  const children = new Map();
  list.forEach((turn, turnIndex) => {
    (turn?.blocks || []).forEach((b, blockIndex) => {
      if (b?.type === "tool_result" && b.tool_use_id) {
        const at = results.get(b.tool_use_id);
        if (at) at.push({ turnIndex, block: b });
        else results.set(b.tool_use_id, [{ turnIndex, block: b }]);
        if (!firstResult.has(b.tool_use_id)) firstResult.set(b.tool_use_id, b);
      } else if (b?.type === "tool_use" && b.virtual && b.codeMode?.parent) {
        const at = children.get(b.codeMode.parent);
        const entry = { block: b, turnIndex, blockIndex };
        if (at) at.push(entry);
        else children.set(b.codeMode.parent, [entry]);
      }
    });
  });
  const resultOf = (id, fromTurn) => (results.get(id) || []).find((r) => r.turnIndex >= fromTurn)?.block ?? null;
  let waitEnvByCell = null;

  const actions = [];
  const byId = new Map();
  list.forEach((turn, turnIndex) => {
    (turn?.blocks || []).forEach((b, blockIndex) => {
      if (b?.type !== "tool_use" || b.virtual) return;
      const id = b.id ?? `pos:${turnIndex}:${blockIndex}`;
      const cell = isCodeModeCell(b);
      const form = cell ? "cell" : shellToolCommand(b.name, b.input) ? "shell" : "tool";
      const copies = b.id ? children.get(b.id) : null;
      let calls;
      if (copies?.length) {
        calls = copies.map((c) => callOf(c.block, c.turnIndex, c.blockIndex, pairedOf(resultOf(c.block.id, turnIndex))));
      } else if (cell) {
        // Not expanded. The wrapper's own argument still states the command,
        // and the cell made exactly that one call, so its stdout is the call's.
        const args = codexWrappedArgs(b.input.input);
        const command = args ? normalizeShellCommand(args.cmd ?? args.command) : null;
        if (command) {
          const result = b.id ? resultOf(b.id, turnIndex) : null;
          let paired = UNPAIRED;
          if (result) {
            waitEnvByCell ??= waitEnvelopesByCell(list, firstResult);
            const { text, isError } = cellOutcome(result, waitEnvByCell, true);
            paired = { text: isError ? null : text, ok: !isError, paired: true };
          }
          calls = [{
            ...callOf({ ...b, id }, turnIndex, blockIndex, paired),
            command,
            language: detectShellLanguage(b.name, command),
          }];
        } else {
          calls = [];
        }
      } else {
        calls = [callOf({ ...b, id }, turnIndex, blockIndex, pairedOf(b.id ? resultOf(b.id, turnIndex) : null))];
      }
      const action = { id, turnIndex, blockIndex, tool: String(b.name || "tool"), form, calls };
      actions.push(action);
      byId.set(id, action);
    });
  });
  return { actions, byId };
}

/**
 * The shell commands the actions ran, one entry per call, in trace order.
 * `dropped` says the cap stopped the list rather than the trace running out.
 */
export function actionShellCalls(table) {
  const calls = [];
  for (const action of table?.actions || []) {
    for (const call of action.calls) {
      if (!call.command) continue;
      if (calls.length >= MAX_SHELL_CALLS) return { calls, dropped: true };
      calls.push({
        key: call.key,
        actionId: action.id,
        turnIndex: call.turnIndex,
        blockIndex: call.blockIndex,
        tool: call.name,
        command: call.command,
        language: call.language,
        result: call.result,
      });
    }
  }
  return { calls, dropped: false };
}

/**
 * The action a lane packet came from, or null for a packet no call produced
 * (a prompt, a reply, a note). Every door keys its packets on the block it
 * read, suffixed after a `#`: an extractor's op key, a copy's `<id>#cm0`, the
 * shell grammar's `<call>#<n>`, a verified reading's `<call>#v0`.
 */
export function actionIdOf(item, byId) {
  if (item?.actionId) return item.actionId;
  const id = typeof item?.id === "string" ? item.id : "";
  const key = item?.op?.key ?? (id.includes(":") ? id.slice(id.indexOf(":") + 1) : null);
  if (key == null) return null;
  const base = String(key).split("#")[0];
  return byId?.has(base) ? base : null;
}
